import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

interface URLQueriesOptions {
  defaults?: Record<string, string>;
  updateHistory?: boolean;
}

interface URLQueriesActions {
  getQuery: (key: string) => string | undefined;
  getAllQueries: () => Record<string, string>;
  setQuery: (key: string, value: string) => void;
  setQueries: (queries: Record<string, string>) => void;
  upsertQueries: (queries: Record<string, string | null>) => void;
  removeQuery: (key: string) => void;
  removeQueries: (keys: string[]) => void;
  clearQueries: () => void;
  hasQuery: (key: string) => boolean;
}

export const useUrlQueries = (
  options: URLQueriesOptions = {}
): URLQueriesActions => {
  const { defaults = {}, updateHistory = true } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const getQuery = useCallback(
    (key: string): string | undefined => {
      const value = searchParams.get(key);
      return value ?? defaults[key];
    },
    [searchParams, defaults]
  );

  const getAllQueries = useCallback((): Record<string, string> => {
    const queries: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      queries[key] = value;
    });
    return { ...defaults, ...queries };
  }, [searchParams, defaults]);

  const setQuery = useCallback(
    (key: string, value: string) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set(key, value);
      setSearchParams(newParams, { replace: !updateHistory });
    },
    [searchParams, setSearchParams, updateHistory]
  );

  const setQueries = useCallback(
    (queries: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(queries).forEach(([key, value]) => {
        newParams.set(key, value);
      });
      setSearchParams(newParams, { replace: !updateHistory });
    },
    [searchParams, setSearchParams, updateHistory]
  );

  const upsertQueries = useCallback(
    (queries: Record<string, string | null | undefined>) => {
      const newParams = new URLSearchParams(searchParams);

      Object.entries(queries).forEach(([key, value]) => {
        if (value == null || value === "") {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
      });

      setSearchParams(newParams, { replace: !updateHistory });
    },
    [searchParams, setSearchParams, updateHistory]
  );

  const removeQuery = useCallback(
    (key: string) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete(key);
      setSearchParams(newParams, { replace: !updateHistory });
    },
    [searchParams, setSearchParams, updateHistory]
  );

  const removeQueries = useCallback(
    (keys: string[]) => {
      const newParams = new URLSearchParams(searchParams);
      keys.forEach((key) => newParams.delete(key));
      setSearchParams(newParams, { replace: !updateHistory });
    },
    [searchParams, setSearchParams, updateHistory]
  );

  const clearQueries = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: !updateHistory });
  }, [setSearchParams, updateHistory]);

  const hasQuery = useCallback(
    (key: string): boolean => {
      return searchParams.has(key);
    },
    [searchParams]
  );

  return useMemo(
    () => ({
      getQuery,
      getAllQueries,
      setQuery,
      setQueries,
      upsertQueries,
      removeQuery,
      removeQueries,
      clearQueries,
      hasQuery,
    }),
    [
      getQuery,
      getAllQueries,
      setQuery,
      setQueries,
      upsertQueries,
      removeQuery,
      removeQueries,
      clearQueries,
      hasQuery,
    ]
  );
};
